<template>
  <aside class="dialog-vc" :class="{ 'visible': computedVisible }" tabindex="-1">
    <transition name="dialog-vc__backdrop-" :duration="{ leave: 250 }">
      <div v-if="computedVisible" class="dialog-vc__backdrop"></div>
    </transition>
    <transition name="dialog-vc__content-" :duration="{ leave: 250 }">
      <form
        v-if="computedVisible"
        ref="form"
        class="dialog-vc__content"
        @submit.prevent="handleSubmit"
      >
        <component
          :disabled="validating"
          :is="computedCurrentComponent"
          v-bind="currentComponentProps"
          @submit="handleSubmit"
        ></component>
        <div class="dialog-vc__buttons">
          <HUIButton
            :disabled="validating"
            class="dialog-vc__button"
            v-if="currentType === 'prompt' || currentType === 'confirm'"
            @click="handleCancelClick"
          >{{ cancelLabel }}</HUIButton>
          <span
            v-if="currentType === 'prompt' || currentType === 'confirm'"
            class="dialog-vc__buttons-separator"
          ></span>
          <HUIButton
            :disabled="validating"
            :spinner="validating"
            type="submit"
            class="dialog-vc__button"
          >{{ okLabel }}</HUIButton>
        </div>
      </form>
    </transition>
  </aside>
</template>

<script>
import { mapState } from 'vuex'
import { HUIButton } from '@/halifax-ui'
import Prompt from '@/containers/DialogVC/Prompt'
import Alert from '@/containers/DialogVC/Alert'
import Confirm from '@/containers/DialogVC/Confirm'
import getFormData from '@/utils/getFormData'

export default {
  components: {
    HUIButton
  },
  data() {
    return {
      validating: false
    }
  },
  computed: {
    ...mapState('dialog', {
      currentComponent: 'current_component',
      currentComponentProps: 'current_component_props',
      currentType: 'current_type',
      visible: 'visible'
    }),
    computedCurrentComponent() {
      const strategy = {
        alert: Alert,
        confirm: Confirm,
        prompt: Prompt
      }
      return this.currentComponent || strategy[this.currentType]
    },
    computedVisible() {
      return this.visible && this.computedCurrentComponent
    },
    okLabel() {
      return (this.currentComponentProps && this.currentComponentProps.ok) || 'Ok'
    },
    cancelLabel() {
      return (this.currentComponentProps && this.currentComponentProps.cancel) || 'Annullér'
    }
  },
  watch: {
    computedVisible(visible) {
      if (visible) {
        this.$el.focus()
      }
    }
  },
  methods: {
    handleCancelClick() {
      this.$store.dispatch('dialog/HIDE')
    },
    handleSubmit(event) {
      const okStrategy = {
        alert: undefined,
        confirm: true,
        prompt: {}
      }
      const payload = okStrategy[this.currentType]

      if (this.currentType === 'prompt') {
        const formData = getFormData(this.$refs.form)

        formData.forEach(pair => {
          const [key, value] = pair

          if (payload[key]) {
            payload[key] = [payload[key]]
          }

          if (payload[key] instanceof Array) {
            payload[key].push(value)
          } else {
            payload[key] = value
          }
        })
      }

      // Only close if customValidationOnSubmit isn't set
      if (typeof this.currentComponentProps.asyncValidator === 'function') {
        this.validating = true
        this.currentComponentProps
          .asyncValidator(payload)
          .then(() => (this.validating = false))
          .catch(() => (this.validating = false))
      } else {
        this.$store.dispatch('dialog/HIDE', payload)
      }
    }
  }
}
</script>

<style lang="scss">
@import '~@/scss/variables';
@import '~@/halifax-ui/src/scss/framework';

.dialog-vc {
  $module: '.dialog-vc';

  pointer-events: none;
  position: fixed;
  height: 100vh;
  width: 100%;
  top: 0;
  left: 0;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  z-index: $z-index-dialog-vc;
  opacity: 0;
  transition-duration: 250ms;

  &.visible {
    pointer-events: auto;
    transition: opacity 0ms;
    opacity: 1;
  }
  &--leave-active {
    transition: opacity 1300ms ease;
  }
  &--leave-to  {
    opacity: 0;
  }
  &__content {
    position: relative;
    overflow: hidden;
    border-radius: 6px;
    width: 100%;
    min-width: 240px;
    max-width: 300px;
    margin: $padding-x-container - 1px;
    border: 1px solid hsla(0, 0%, 100%, 0.01);
    box-shadow: 0 0 25px hsla(0, 0%, 0%, 0.15);

    &--enter-active {
      transition: transform 325ms $easeOutCirc, opacity 300ms ease;
      transition-delay: 75ms, 75ms;
    }
    &--enter {
      transform: scale(1.25);
      opacity: 0;
    }
  }
  &__backdrop {
    background: hsla(193, 9%, 14%, 0.85);
    position: fixed;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    will-change: opacity;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;

    &--enter-active {
      transition: opacity 325ms ease;
    }
    &--enter {
      opacity: 0;
    }
  }

  &__buttons {
    display: flex;
    justify-content: stretch;
    align-items: center;
    background-color: $text-color;
    overflow: hidden;
  }
  &__button {
    margin: 0;
    font-size: 1.8rem !important;

    &:first-of-type {
      border-radius: 0 0 0 5px !important;
    }
    &:last-of-type {
      border-radius: 0 0 5px 0 !important;
    }

    &[disabled],
    form[disabled] & {
      background-color: mix($text-color, $background-color, 95%) !important;
    }
  }
  &__buttons-separator {
    width: 1px;
    height: 60px;
    flex: 0 0 1px;
    background-color: hsla(0, 0%, 100%, 0.03);
  }
}
</style>
